import type { Anbefaling, AnbefaltForvaltningType } from "./oversikt/useAnbefaling";
import spareprofiler, { FORSIKTIG, LOFAVOR, MODERAT, AKSJER, ALDERSTILPASSET, OFFENSIV, RENTER, type Spareprofil, getSpareprofil } from "../investeringsvalg/spareprofiler";
import { track } from "~/shared/api/server";
import type { ForvaltningsForm } from "~/shared/components/investeringsvalg/forvaltningsformer";
import type { RisikovurderingMapping, RisikovurderingSporsmal } from "~/shared/reducers/kartlegging";
import type { Prices } from "~/shared/reducers/prices";
import type { VeiledningsSvar } from "~/utils/kartlegging";
import { isPSK } from "~/utils/policies";
import type { F2100PolicyDetailsDto } from "~/shared/api/usePolicyDetails";

export function medBaerekraft(produktId: string) {
	return isPSK(produktId);
}

interface GetForvaltning {
	forvaltning: ForvaltningsForm;
	anbefaltForvaltningType: AnbefaltForvaltningType;
}
function getForvaltning(policy?: F2100PolicyDetailsDto): GetForvaltning {
	if (policy) {
		return { forvaltning: policy.forvaltning, anbefaltForvaltningType: "EKSISTERENDE" };
	}
	return { forvaltning: undefined as unknown as ForvaltningsForm, anbefaltForvaltningType: "VALGT" }; // blir overskrevet i bærekraftveiledning
}

function getOverridenSpareprofil(recommendedSpareprofil: Spareprofil, age: number, policy?: F2100PolicyDetailsDto, prices?: Prices) {
	if (!(policy && prices)) {
		return undefined;
	}

	const availableSpareprofiler = Object.keys(prices.forvaltningskost[policy.forvaltning || policy.defaultForvaltning].spareprofiler) as Spareprofil[];
	const recommendationAvailable = availableSpareprofiler.includes(recommendedSpareprofil);

	if (recommendationAvailable) {
		return undefined;
	}

	const maxAksjeandel = availableSpareprofiler
		.map((profil) => getSpareprofil(profil).sharePercentage)
		.sort()
		.pop() as number;

	const recommendedAksjeandel = getSpareprofil(recommendedSpareprofil).sharePercentage as number;

	let overridenRecommendation = Object.values(spareprofiler)
		.filter((profil) => profil.sharePercentage && profil.sharePercentage <= Math.min(maxAksjeandel, recommendedAksjeandel))
		.sort((a, b) => (a.sharePercentage !== b.sharePercentage ? (a.sharePercentage as number) - (b.sharePercentage as number) : 0))
		.pop()?.id;

	if (overridenRecommendation === OFFENSIV && age > 53) {
		overridenRecommendation = MODERAT;
	}

	track("risikovurdering", "not-available", {
		age,
		product: policy.produktId,
		productType: policy.produkttype,
		recommendation: recommendedSpareprofil,
		replacement: overridenRecommendation,
	});

	return overridenRecommendation;
}

interface GetAnbefaling {
	anbefaling: Anbefaling;
	anbefaltAndelProposjon: number;
	anbefaltSpareprofilIkkeTilgjengelig: boolean;
}

export function getAnbefaling(
	svar: VeiledningsSvar[],
	sporsmal: RisikovurderingSporsmal[],
	mapping: RisikovurderingMapping,
	age: number,
	policy?: F2100PolicyDetailsDto,
	prices?: Prices,
): GetAnbefaling {
	const forvaltning = getForvaltning(policy);

	const recommendedSpareprofil: Spareprofil = sporsmal.length === 3 ? mapping[svar[0]][svar[1]][svar[2]] : mapping[svar[0]][svar[1]];

	const overridenRecommendation = getOverridenSpareprofil(recommendedSpareprofil, age, policy, prices);

	const spareprofil = overridenRecommendation || recommendedSpareprofil;

	const anbefaltAndelProposjon: number = recommendedPercentages[spareprofil]?.[Math.min(67, age)]?.rounded || getSpareprofil(spareprofil).sharePercentage;

	return {
		anbefaling: {
			forvaltning: forvaltning.forvaltning,
			spareprofil,
			anbefaltForvaltningType: forvaltning.anbefaltForvaltningType,
		},
		anbefaltAndelProposjon,
		anbefaltSpareprofilIkkeTilgjengelig: !!overridenRecommendation,
	};
}

export const recommendedPercentages = {
	[RENTER]: {}, // Share percentage never changes with age
	[FORSIKTIG]: {
		58: { actual: 24, rounded: 25 },
		59: { actual: 22, rounded: 20 },
		60: { actual: 21, rounded: 20 },
		61: { actual: 19, rounded: 20 },
		62: { actual: 18, rounded: 15 },
		63: { actual: 16, rounded: 15 },
		64: { actual: 15, rounded: 15 },
		65: { actual: 13, rounded: 15 },
		66: { actual: 12, rounded: 10 },
		67: { actual: 10, rounded: 10 },
	},
	[MODERAT]: {
		58: { actual: 47, rounded: 45 },
		59: { actual: 44, rounded: 45 },
		60: { actual: 41, rounded: 40 },
		61: { actual: 38, rounded: 40 },
		62: { actual: 35, rounded: 35 },
		63: { actual: 32, rounded: 30 },
		64: { actual: 29, rounded: 30 },
		65: { actual: 26, rounded: 25 },
		66: { actual: 23, rounded: 25 },
		67: { actual: 20, rounded: 20 },
	},
	[OFFENSIV]: {
		58: { actual: 71, rounded: 70 },
		59: { actual: 66, rounded: 65 },
		60: { actual: 62, rounded: 60 },
		61: { actual: 57, rounded: 55 },
		62: { actual: 53, rounded: 50 },
		63: { actual: 48, rounded: 50 },
		64: { actual: 44, rounded: 45 },
		65: { actual: 39, rounded: 40 },
		66: { actual: 35, rounded: 35 },
		67: { actual: 30, rounded: 30 },
	},
	[AKSJER]: {
		58: { actual: 94, rounded: 95 },
		59: { actual: 88, rounded: 90 },
		60: { actual: 82, rounded: 80 },
		61: { actual: 76, rounded: 75 },
		62: { actual: 70, rounded: 70 },
		63: { actual: 64, rounded: 65 },
		64: { actual: 58, rounded: 60 },
		65: { actual: 52, rounded: 50 },
		66: { actual: 46, rounded: 45 },
		67: { actual: 40, rounded: 40 },
	},
	[ALDERSTILPASSET]: {
		41: { actual: 97, rounded: 95 },
		42: { actual: 94, rounded: 95 },
		43: { actual: 91, rounded: 90 },
		44: { actual: 88, rounded: 90 },
		45: { actual: 85, rounded: 85 },
		46: { actual: 82, rounded: 80 },
		47: { actual: 79, rounded: 80 },
		48: { actual: 76, rounded: 75 },
		49: { actual: 73, rounded: 75 },
		50: { actual: 70, rounded: 70 },
		51: { actual: 67, rounded: 65 },
		52: { actual: 64, rounded: 65 },
		53: { actual: 61, rounded: 60 },
		54: { actual: 58, rounded: 60 },
		55: { actual: 55, rounded: 55 },
		56: { actual: 52, rounded: 50 },
		57: { actual: 50, rounded: 50 },
		58: { actual: 47, rounded: 45 },
		59: { actual: 44, rounded: 45 },
		60: { actual: 41, rounded: 40 },
		61: { actual: 38, rounded: 40 },
		62: { actual: 35, rounded: 35 },
		63: { actual: 32, rounded: 30 },
		64: { actual: 29, rounded: 30 },
		65: { actual: 26, rounded: 25 },
		66: { actual: 23, rounded: 25 },
		67: { actual: 20, rounded: 20 },
	},
	[LOFAVOR]: {
		41: { actual: 97, rounded: 95 },
		42: { actual: 94, rounded: 95 },
		43: { actual: 91, rounded: 90 },
		44: { actual: 88, rounded: 90 },
		45: { actual: 85, rounded: 85 },
		46: { actual: 82, rounded: 80 },
		47: { actual: 79, rounded: 80 },
		48: { actual: 76, rounded: 75 },
		49: { actual: 73, rounded: 75 },
		50: { actual: 70, rounded: 70 },
		51: { actual: 67, rounded: 65 },
		52: { actual: 64, rounded: 65 },
		53: { actual: 61, rounded: 60 },
		54: { actual: 58, rounded: 60 },
		55: { actual: 55, rounded: 55 },
		56: { actual: 52, rounded: 50 },
		57: { actual: 50, rounded: 50 },
		58: { actual: 47, rounded: 45 },
		59: { actual: 44, rounded: 45 },
		60: { actual: 41, rounded: 40 },
		61: { actual: 38, rounded: 40 },
		62: { actual: 35, rounded: 35 },
		63: { actual: 32, rounded: 30 },
		64: { actual: 29, rounded: 30 },
		65: { actual: 26, rounded: 25 },
		66: { actual: 23, rounded: 25 },
		67: { actual: 20, rounded: 20 },
	},
} as const;
